import { msg } from "@lingui/core/macro";
import {
  createContext,
  createEffect,
  createMemo,
  type JSX,
  onMount,
  type ParentComponent,
  untrack,
  useContext,
} from "solid-js";
import { fromActorRef, useActorRef } from "@xstate/solid";
import {
  type AbstractPowerSyncDatabase,
  appBootMachine,
  createProfileHandlers,
  isSignOutInProgress,
  type Session,
  signOutMachine,
} from "@boveda/shared";
import { useLocation, useNavigate } from "@tanstack/solid-router";
import { readAccountsStateSync } from "@/lib/accounts";
import { defaultActiveIndex, readActiveIndexFromUrl } from "@/lib/active-account";
import { usePowerSyncContext } from "./power-sync-context";
import { useI18n } from "./i18n-provider";
import { useExecuteSignOut, useSession } from "@/lib/auth";
import { api } from "@/lib/api";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";

const AUTH_PATH_PREFIXES = ["/login", "/register", "/forgot-password", "/reset-password"];

function isInAuthGroup(pathname: string): boolean {
  return AUTH_PATH_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

/**
 * Context to expose sign out handlers to the rest of the app.
 */
interface SignOutContextValue {
  promptSignOut: () => void;
  promptQuickSignOut: () => void;
  promptClearData: () => void;
  promptRevokeSession: (sessionId: string) => void;
  isChecking: () => boolean;
  isSigningOut: () => boolean;
}

const SignOutContext = createContext<SignOutContextValue>();

export function useSignOut() {
  const context = useContext(SignOutContext);
  if (!context) {
    throw new Error("useSignOut must be used within SignOutProvider (BootManager)");
  }
  return context;
}

/**
 * BootManager coordinates the loading state of the entire application
 * using appBootMachine from @boveda/shared.
 *
 * It acts as a gatekeeper that only renders the application content
 * once all parallel boot processes (DB, I18n, Fonts, Session) are ready.
 */
export const BootManager: ParentComponent = (props) => {
  const actorRef = useActorRef(appBootMachine);
  const state = fromActorRef(actorRef);

  const ps = usePowerSyncContext();
  const session = useSession();
  const executeSignOut = useExecuteSignOut();
  const location = useLocation();
  const navigate = useNavigate();

  // 1. Database Readiness
  createEffect(() => {
    if (!ps.isLoading() && ps.db()) {
      actorRef.send({ type: "DB_READY" });
    }
  });

  // 2. Resolve non-blocking web resources immediately on mount
  onMount(() => {
    actorRef.send({ type: "I18N_READY" });
    actorRef.send({ type: "FONTS_LOADED" });
    actorRef.send({ type: "TYPOGRAPHY_READY" });
    actorRef.send({ type: "THEME_READY" });
  });

  // 5. Session & Auth Group Detection
  createEffect(() => {
    const { isPending } = session;
    const hasSession = session.isAuthenticated();
    const loc = location();
    // Multi-account: `/login?mode=add` is reached BY an already-authenticated
    // user who wants to add a second account. Treat it as NOT in the auth group
    // so the boot machine's "authenticated + in auth group → redirect home" rule
    // doesn't bounce them out of the add-account login before they can sign in.
    const isAddingAccount = new URLSearchParams(loc.searchStr ?? "").get("mode") === "add";
    const inAuthGroup = !isAddingAccount && isInAuthGroup(loc.pathname);

    if (!isPending) {
      actorRef.send({
        type: "SESSION_RESOLVED",
        hasSession,
        inAuthGroup,
      });
    }
  });

  // 6. Centralized Redirection (authenticated user on an auth page → app).
  // Per-tab multi-account: target the active account's `/u/<index>`. Prefer the
  // index already in THIS tab's URL (a deep link), else the registry default.
  createEffect(() => {
    if (state().matches("redirecting") && untrack(() => session.isAuthenticated())) {
      const index = readActiveIndexFromUrl() ?? defaultActiveIndex(readAccountsStateSync()) ?? 0;
      // Typed param form (`to: "/u/$u"` + `params`): a flat `to: "/u/<n>/"` is a
      // no-op when this effect fires while already inside the `/u/$u` tree.
      void navigate({ to: "/u/$u", params: { u: index } });
    }
  });

  const isReady = () => state().matches("ready") || state().matches("redirecting");

  return (
    <SignOutProvider
      db={ps.db()}
      session={session.data}
      disconnectAndClear={ps.disconnectAndClear}
      isReady={isReady()}
      onRedirectToSignIn={() => {
        executeSignOut();
      }}
    >
      {props.children}
    </SignOutProvider>
  );
};

function SignOutProvider(props: {
  db: AbstractPowerSyncDatabase | null;
  session: Session | null | undefined;
  disconnectAndClear: () => Promise<void>;
  isReady: boolean;
  onRedirectToSignIn: () => void;
  children: JSX.Element;
}) {
  const { t } = useI18n();

  const signOutInput = createMemo(() => ({
    db: props.db,
    session: props.session,
    // On web, useExecuteSignOut (triggered by onRedirectToSignIn) handles the
    // database cleanup and server sign-out in a non-blocking background task.
    // We pass no-ops here to prevent duplicate execution, database contention
    // and network/timeout races.
    disconnectAndClear: async () => {
      // no-op on web: handled by executeSignOut
    },
    onSignOut: async () => {
      // no-op on web: handled by executeSignOut
    },
    onRedirectToSignIn: props.onRedirectToSignIn,
    onRevokeSession: async (sessionId: string) => {
      await api.POST("/api/auth/revoke-session", { body: { id: sessionId } });
    },
  }));

  const signOutActor = useActorRef(signOutMachine, {
    input: untrack(() => signOutInput()),
  });

  const signOutState = fromActorRef(signOutActor);
  const profileHandlers = createProfileHandlers(signOutActor.send);

  const isStandardSignOut = () => signOutState().matches("confirmSignOut");
  const isClearConfirm = () => signOutState().matches("confirmClear");
  const isRevokeConfirm = () => signOutState().matches("confirmRevoke");
  const isChecking = () => signOutState().matches("checkingUnsynced");
  const isSigningOut = () => isSignOutInProgress(signOutState());
  const hasUnsyncedChanges = () => signOutState().context.hasUnsyncedChanges;

  const confirmSignOut = () => {
    signOutActor.send({ type: "CONFIRMED" });
  };

  const cancelSignOut = () => {
    signOutActor.send({ type: "CANCELLED" });
  };

  return (
    <SignOutContext.Provider
      value={{
        promptSignOut: profileHandlers.promptSignOut,
        promptQuickSignOut: profileHandlers.promptQuickSignOut,
        promptClearData: profileHandlers.promptClearData,
        promptRevokeSession: profileHandlers.promptRevokeSession,
        isChecking,
        isSigningOut,
      }}
    >
      {props.children}

      <ConfirmDialog
        open={isStandardSignOut() || isSigningOut()}
        onOpenChange={(open) => {
          if (!open && !isSigningOut()) {
            cancelSignOut();
          }
        }}
        title={hasUnsyncedChanges() ? t(msg`Unsynced Changes`) : t(msg`Sign Out?`)}
        description={
          hasUnsyncedChanges()
            ? t(
                "You have changes that haven't been synced to the server yet. If you sign out now, these changes will be permanently lost.",
              )
            : t(msg`Are you sure you want to sign out on this device?`)
        }
        confirmLabel={hasUnsyncedChanges() ? t(msg`Sign Out Anyway`) : t(msg`Sign Out`)}
        variant="destructive"
        onConfirm={confirmSignOut}
        isLoading={isSigningOut()}
      />

      <ConfirmDialog
        open={isClearConfirm() || signOutState().matches("clearing")}
        onOpenChange={(open) => {
          if (!open && !signOutState().matches("clearing")) {
            cancelSignOut();
          }
        }}
        title={t(msg`Clear local data`)}
        description={t(
          "This will delete all your local vault data. This action cannot be undone. Continue?",
        )}
        confirmLabel={t(msg`Delete Everything`)}
        variant="destructive"
        onConfirm={confirmSignOut}
        isLoading={signOutState().matches("clearing")}
      />

      <ConfirmDialog
        open={isRevokeConfirm() || signOutState().matches("revoking")}
        onOpenChange={(open) => {
          if (!open && !signOutState().matches("revoking")) {
            cancelSignOut();
          }
        }}
        title={t(msg`Revoke Session`)}
        description={t(msg`This will sign out the device immediately. Continue?`)}
        confirmLabel={t(msg`Revoke`)}
        variant="destructive"
        onConfirm={confirmSignOut}
        isLoading={signOutState().matches("revoking")}
      />
    </SignOutContext.Provider>
  );
}
